import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  SwipeableDrawer,
  IconButton,
  Divider,
  Button
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";

import type { TestingSession } from "@/data/mock-data";
import { formatPct } from "@/lib/formatters";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Props -----------------------------------------------------------------

interface TestingSessionDrawerProps {
  /** Non-null → drawer open showing this backtest session. */
  session: TestingSession | null;
  onClose: () => void;
}

// ---- Component -------------------------------------------------------------

/**
 * Backtest-session detail — bottom-sheet drawer. Replaces the old
 * `/testing/:id` page (Sergey 2026-05-13: sub-views are drawers, not
 * pages). Single view (no tabs), so it sizes to its content with a
 * max-height cap rather than the fixed 92vh the tabbed drawers use.
 */
export function TestingSessionDrawer({
  session,
  onClose
}: TestingSessionDrawerProps) {
  // Keep the last session through the slide-out animation.
  const [snapshot, setSnapshot] = useState<TestingSession | null>(session);
  if (session && session !== snapshot) setSnapshot(session);

  const s = snapshot;
  if (!s) return null;

  const returnPositive = s.return >= 0;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={session !== null}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "88vh",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Close (×) */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={(t: Theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: alpha(t.palette.text.primary, 0.08),
          "&:hover": { bgcolor: alpha(t.palette.text.primary, 0.16) }
        })}
      >
        <CloseRounded sx={{ fontSize: 22 }} />
      </IconButton>

      {/* Header */}
      <Stack spacing={0.5} sx={{ px: 2, pt: 1, pb: 1.5, pr: 7 }}>
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}
        >
          Backtest session
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {s.botName}
        </Typography>
        <Typography
          variant="h5"
          sx={(t) => ({
            fontWeight: 800,
            fontFeatureSettings: '"tnum" 1',
            color: returnPositive ? t.palette.success.main : t.palette.error.main
          })}
        >
          {formatPct(s.return)}
        </Typography>
      </Stack>

      <Divider />

      {/* Detail rows */}
      <Box sx={{ px: 2, py: 1.5, overflowY: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            rowGap: 1,
            columnGap: 2,
            alignItems: "center"
          }}
        >
          <Row label="Total return">
            <ToneValue positive={returnPositive}>
              {formatPct(s.return)}
            </ToneValue>
          </Row>
          <Row label="Win rate">
            {formatPct(s.winRate, { signed: false })}
          </Row>
          <Row label="Trades">{String(s.trades)}</Row>
          <Row label="Portfolio">{s.portfolio}</Row>
          <Row label="Date range">{s.notes}</Row>
          <Row label="Started">{s.startedAt}</Row>
        </Box>

        {/* Placeholder note — the full backtest detail (equity curve,
         *  drawdown chart, trade-by-trade log) is on the roadmap. */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "action.hover",
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start"
          }}
        >
          <ScienceOutlined sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            The full equity curve, drawdown chart, and trade-by-trade log
            for this session render here when the backtest detail is
            fleshed out.
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Action bar */}
      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ fontWeight: 600 }}
        >
          Close
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

// ---- Internals -------------------------------------------------------------

function Row({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Typography variant="body1" sx={{ color: "text.secondary", pr: 2 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.primary",
          textAlign: "right",
          fontFeatureSettings: '"tnum" 1',
          wordBreak: "break-word"
        }}
      >
        {children}
      </Typography>
    </>
  );
}

function ToneValue({
  positive,
  children
}: {
  positive: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="span"
      sx={(t) => ({
        fontWeight: 700,
        color: positive ? t.palette.success.main : t.palette.error.main
      })}
    >
      {children}
    </Box>
  );
}
